import {Region} from '@/utils/countries'
import {routeTo} from '@/utils/router'

import {OfficeCountry} from './officeLocations'

export enum PhoneNumber {
  SK = '(+421) 2 3345 6574',
  UK = '(+44) 74 8888 2146',
  US = '(+1) 929-810-2266',
  // No trunk prefix after the country code: `+61 08 ...` is not dialable. `(08) 9106 9499` is
  // the domestic form, `+61 8 9106 9499` the international one - these values are shown with
  // `(+61)`, so they are the international form.
  AU = '(+61) 8 9106 9499',
}

/*
 * Which support line each region is routed to. Exported so anything that needs the inverse - the
 * regions a number serves - derives it from here rather than restating it.
 */
export const phoneNumberByRegion: Partial<Record<Region, PhoneNumber>> = {
  [Region.SK]: PhoneNumber.SK,
  [Region.EU]: PhoneNumber.SK,
  [Region.DE]: PhoneNumber.SK,
  [Region.IE]: PhoneNumber.SK,
  [Region.BX]: PhoneNumber.SK,
  [Region.FR]: PhoneNumber.SK,
  [Region.CH]: PhoneNumber.SK,
  [Region.NO]: PhoneNumber.SK,
  [Region.IT]: PhoneNumber.SK,
  [Region.ES]: PhoneNumber.SK,
  [Region.DK]: PhoneNumber.SK,
  [Region.AR]: PhoneNumber.SK,
  [Region.BG]: PhoneNumber.SK,
  [Region.EE]: PhoneNumber.SK,
  [Region.GE]: PhoneNumber.SK,
  [Region.IL]: PhoneNumber.SK,
  [Region.KZ]: PhoneNumber.SK,
  [Region.LV]: PhoneNumber.SK,
  [Region.LT]: PhoneNumber.SK,
  [Region.PL]: PhoneNumber.SK,
  [Region.PT]: PhoneNumber.SK,
  [Region.RO]: PhoneNumber.SK,
  [Region.TR]: PhoneNumber.SK,
  [Region.UA]: PhoneNumber.SK,
  [Region.SE]: PhoneNumber.SK,
  [Region.FI]: PhoneNumber.SK,
  [Region.HU]: PhoneNumber.SK,
  [Region.AT]: PhoneNumber.SK,
  [Region.SI]: PhoneNumber.SK,
  [Region.HR]: PhoneNumber.SK,
  [Region.MC]: PhoneNumber.SK,
  [Region.AD]: PhoneNumber.SK,
  [Region.CZ]: PhoneNumber.SK,
  [Region.US]: PhoneNumber.US,
  [Region.CA]: PhoneNumber.US,
  [Region.SG]: PhoneNumber.US,
  [Region.HK]: PhoneNumber.US,
  [Region.IN]: PhoneNumber.US,
  [Region.CN]: PhoneNumber.US,
  [Region.BR]: PhoneNumber.US,
  [Region.TH]: PhoneNumber.US,
  [Region.ID]: PhoneNumber.US,
  [Region.TW]: PhoneNumber.US,
  [Region.BD]: PhoneNumber.US,
  [Region.JP]: PhoneNumber.US,
  [Region.MX]: PhoneNumber.US,
  [Region.VN]: PhoneNumber.US,
  [Region.CO]: PhoneNumber.US,
  [Region.EC]: PhoneNumber.US,
  [Region.PE]: PhoneNumber.US,
  [Region.EG]: PhoneNumber.US,
  [Region.JO]: PhoneNumber.US,
  [Region.KE]: PhoneNumber.US,
  [Region.LB]: PhoneNumber.US,
  [Region.MA]: PhoneNumber.US,
  [Region.NG]: PhoneNumber.US,
  [Region.PK]: PhoneNumber.US,
  [Region.TN]: PhoneNumber.US,
  [Region.TZ]: PhoneNumber.US,
  [Region.AE]: PhoneNumber.US,
  [Region.MY]: PhoneNumber.US,
  [Region.PH]: PhoneNumber.US,
  [Region.SA]: PhoneNumber.US,
  [Region.NZ]: PhoneNumber.AU,
  [Region.AU]: PhoneNumber.AU,
}

// Everything not routed explicitly falls back to this line.
export const FALLBACK_PHONE_NUMBER = PhoneNumber.UK

export const getPhoneNumber = (region: Region) => {
  const phoneNumber = phoneNumberByRegion[region] ?? FALLBACK_PHONE_NUMBER

  return {phoneNumber, phoneNumberHref: `tel:${phoneNumber}`}
}

const phoneNumberByOffice = {
  [OfficeCountry.SK]: {
    default: PhoneNumber.SK,
    href: `tel:${PhoneNumber.SK}`,
  },
  [OfficeCountry.CZ]: {
    default: PhoneNumber.SK,
    href: `tel:${PhoneNumber.SK}`,
  },
  [OfficeCountry.UK]: {
    default: PhoneNumber.UK,
    href: `tel:${PhoneNumber.UK}`,
  },
  [OfficeCountry.US]: {
    default: PhoneNumber.US,
    href: `tel:${PhoneNumber.US}`,
  },
  [OfficeCountry.AU]: {
    default: PhoneNumber.AU,
    href: `tel:${PhoneNumber.AU}`,
  },
}

export const socials = {
  linkedIn: {
    title: 'LinkedIn',
    imageSrc: '/images/icons/social/linkedin.svg',
    imageAlt: 'LinkedIn icon',
    linkHref: 'https://www.linkedin.com/company/tramatrademarks',
  },
  facebook: {
    title: 'Facebook',
    imageSrc: '/images/icons/social/facebook.svg',
    imageAlt: 'Facebook icon',
    linkHref: 'https://www.facebook.com/tramatrademarks',
  },
  instagram: {
    title: 'Instagram',
    imageSrc: '/images/icons/social/instagram.svg',
    imageAlt: 'Instagram icon',
    linkHref: 'https://www.instagram.com/tramatrademarks/',
  },
  X: {
    title: 'X',
    imageSrc: '/images/icons/social/x.svg',
    imageAlt: 'X icon',
    linkHref: 'https://x.com/tramaTrademarks',
  },
  youtube: {
    imageSrc: '/images/icons/social/youtube.svg',
    imageAlt: 'Youtube icon',
    linkHref: 'https://www.youtube.com/@Tramatm',
  },
}

export const getContactContent = () => {
  return {
    phoneNumberByOffice: phoneNumberByOffice,
    emailAddress: 'support@tramatm.com',
    emailAddressHref: 'mailto:support@tramatm.com',
    consultationLinkText: 'page.ver-contact-banner-consultation-title',
    consultationLinkHref: routeTo.bookConsultation(),
    beforeCloseModal: {
      title: 'exit-popup-title',
      subtitle: 'exit-popup-subtitle',
      imageSrc: '/images/contact.svg',
      imageAlt: 'Email and phone contact',
    },
    socials,
    contactBanner: {
      imageSrc: '/images/advice.svg',
      imageAlt: 'Advice icon',
      title: 'contact-banner-title',
      description: 'contact-banner-desc',
    },
  }
}
